import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Clock,
  LayoutDashboard,
  Users,
  FolderKanban,
  ClipboardCheck,
  FileText,
  Sun,
  Moon,
  Menu,
  X,
  LogOut,
  ChevronRight,
  UserCircle,
  Building2,
  CalendarClock,
  TrendingUp,
  DollarSign,
  Key,
  Plane,
  Trash2,
  BarChart2,
  Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navigation = [
  // CLIENT_MANAGER exclusive items
  { name: 'Client Dashboard', href: '/client-dashboard', icon: BarChart2, roles: ['CLIENT_MANAGER'] },
  { name: 'Client Settings', href: '/client-settings', icon: Settings, roles: ['CLIENT_MANAGER'] },
  // Standard items (NOT for CLIENT_MANAGER)
  { name: 'Time Entry', href: '/timesheet', icon: Clock, roles: ['ADMIN', 'MANAGER', 'EMPLOYEE'] },
  { name: 'My Summary', href: '/summary', icon: LayoutDashboard, roles: ['ADMIN', 'MANAGER', 'EMPLOYEE'] },
  { name: 'Review Queue', href: '/review', icon: ClipboardCheck, roles: ['ADMIN', 'MANAGER'] },
  { name: 'Planning', href: '/planning', icon: TrendingUp, roles: ['ADMIN', 'MANAGER', 'EMPLOYEE'] },
  { name: 'Users', href: '/users', icon: Users, roles: ['ADMIN'] },
  { name: 'Schedules', href: '/schedules', icon: CalendarClock, roles: ['ADMIN'] },
  { name: 'Clients', href: '/clients', icon: Building2, roles: ['ADMIN'] },
  { name: 'Projects', href: '/projects', icon: FolderKanban, roles: ['ADMIN'] },
  { name: 'Assignments', href: '/assignments', icon: UserCircle, roles: ['ADMIN', 'MANAGER'] },
  { name: 'Reports', href: '/reports', icon: FileText, roles: ['ADMIN', 'MANAGER', 'CLIENT_MANAGER'] },
  { name: 'Financials', href: '/financials', icon: DollarSign, roles: ['ADMIN'] },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [vacationDates, setVacationDates] = useState([]);
  const [newVacation, setNewVacation] = useState({ startDate: '', endDate: '' });
  const [loadingVacation, setLoadingVacation] = useState(false);

  const filteredNav = navigation.filter(item => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return item.roles.some(role => userRoles.includes(role));
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchVacationDates = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API_URL}/api/employee-schedules/${user.id}`);
      setVacationDates(response.data?.vacation_dates || []);
    } catch (error) {
      console.error('Failed to load vacation dates:', error);
      setVacationDates([]);
    }
  };

  const handleAddVacation = async () => {
    if (!newVacation.startDate || !newVacation.endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setLoadingVacation(true);
    try {
      await axios.post(`${API_URL}/api/users/${user.id}/vacation`, {
        start_date: newVacation.startDate,
        end_date: newVacation.endDate
      });
      toast.success('Vacation dates added successfully');
      setNewVacation({ startDate: '', endDate: '' });
      await fetchVacationDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vacation dates');
    } finally {
      setLoadingVacation(false);
    }
  };

  const handleDeleteVacation = async (date) => {
    setLoadingVacation(true);
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}/vacation/${date}`);
      toast.success('Vacation date removed');
      await fetchVacationDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove vacation date');
    } finally {
      setLoadingVacation(false);
    }
  };

  const handleVacationDialogOpen = () => {
    fetchVacationDates();
    setVacationDialogOpen(true);
  };


  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-background border-r border-sidebar-border/50 transform transition-transform duration-200 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-sidebar-border/50">
            <Link to="/timesheet" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center dark:shadow-[0_0_12px_hsl(174_72%_50%/0.3)]">
                <Clock className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-semibold tracking-tight">TimeHub</span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
            {filteredNav.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                    isActive 
                      ? "bg-primary text-primary-foreground shadow-sm dark:shadow-[0_0_12px_hsl(174_72%_50%/0.25)]" 
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className={cn("w-5 h-5", isActive && "dark:text-primary-foreground")} />
                  {item.name}
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-sidebar-border/50">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-sidebar-accent transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{(user?.roles || [user?.role]).filter(Boolean).join(', ')}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setChangePasswordOpen(true)} data-testid="change-password-btn">
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleVacationDialogOpen} data-testid="manage-vacation-btn">
                  <Plane className="w-4 h-4 mr-2" />
                  Manage Vacation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                  {theme === 'light' ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="hidden lg:flex lg:items-center lg:gap-3">
              <h1 className="text-lg font-semibold">
                {filteredNav.find(item => item.href === location.pathname)?.name || 'Dashboard'}
              </h1>
              {user?.name && (
                <span className="text-base font-bold text-foreground">
                  — {user.name}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Mobile user name display */}
              <span className="lg:hidden text-base font-bold truncate max-w-32">
                {user?.name}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="hidden lg:flex"
                data-testid="desktop-theme-toggle"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordOpen(false)} disabled={changingPassword}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Vacation Dialog */}
      <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Vacation Days</DialogTitle>
            <DialogDescription>
              Add vacation date ranges and manage your existing vacation days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Add new vacation */}
            <div className="space-y-4 border-b pb-4">
              <h4 className="text-sm font-medium">Add Vacation Dates</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vacation-start">Start Date</Label>
                  <Input
                    id="vacation-start"
                    type="date"
                    value={newVacation.startDate}
                    onChange={(e) => setNewVacation({ ...newVacation, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vacation-end">End Date</Label>
                  <Input
                    id="vacation-end"
                    type="date"
                    value={newVacation.endDate}
                    onChange={(e) => setNewVacation({ ...newVacation, endDate: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={handleAddVacation} disabled={loadingVacation} className="w-full">
                {loadingVacation ? 'Adding...' : 'Add Vacation Dates'}
              </Button>
            </div>

            {/* List existing vacation dates */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Your Vacation Days</h4>
              {vacationDates.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No vacation days scheduled</p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {vacationDates.sort().map((date) => (
                    <div key={date} className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">{new Date(date + 'T00:00:00').toLocaleDateString()}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteVacation(date)}
                        disabled={loadingVacation}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVacationDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
